import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import useSession, { useCustomer } from 'hooks/useSession'
import { ModalBottom } from '@ui/components/Modal'
import Button from '@ui/buttons/Button'
import { useCountries } from 'hooks/options/useCountries'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import { trpc } from 'api'
import { useEffect, useMemo } from 'react'
import FormInput from '@ui/forms/Input/FormInput'
import FormSelect from '@ui/forms/Select/FormSelect'
import { useOrderState } from '@order/hooks/useOrderState'
import { AddressVatSchema } from 'utils/vatSchema'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import {
  AddressSchema,
  BirthdateSchema,
  BirthdateValues,
  LoginUtil,
} from '@ca/common'
import FormInputDate from '@ui/forms/Date/FormInputDate'

import { useConfirmCustomer } from '@user/customer/confirmation/useConfirmCustomer'
import cn from '@ui/utils/cn'

interface Props {
  open: boolean
  close: () => void
}

export default function CustomerModal({ open, close }: Props) {
  const { t } = useTranslation(['ui', 'modals', 'user'])
  const customer = useCustomer()
  const countries = useCountries()
  const address = customer?.address || undefined // TODO: change this to invoice data unce ERP will support it
  const config = useRemoteConfig()
  // TODO: remove first_name last_name once invoice.name field is added
  const defaultValues = {
    first_name: customer?.first_name || '',
    last_name: customer?.last_name || '',
    email: customer?.email || '',
    mobile: customer?.mobile || '',
    phone: customer?.phone || '',
    birth_date: customer?.birth_date
      ? new Date(customer?.birth_date)
      : undefined,
    address: {
      // name: invoice?.name || '',
      // vatId: invoice?.vatId || '',
      street: address?.street || '',
      street2: address?.street2 || '',
      city: address?.city || '',
      zip: address?.zip || '',
      country_code: address?.country_code || '',
      vat: address?.vat || '',
    },
  }

  const hasVat = config?.region?.vat !== 'hidden'

  const schema = useMemo(
    () =>
      z
        .object({
          first_name: z.string().min(2, 'minLength.2'),
          last_name: z.string().min(2, 'minLength.2'),
          birth_date: BirthdateSchema,
          mobile: customer?.mobile
            ? z.string().refine((v) => {
                const phone = LoginUtil.parsePhone(v)
                return phone?.isPossible()
              }, 'validation:mobile.invalid')
            : z.string().optional(),
          email: customer?.email
            ? z.string().email('validation:email.invalid')
            : z
                .string()
                .email('validation:email.invalid')
                .or(z.literal(''))
                .optional(),
          phone: z.string().optional(),
          address: hasVat ? AddressVatSchema : AddressSchema,
        })
        .superRefine((data, ctx) => {
          if (!data.mobile && !data.email && !data.phone) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'validation:requiredGroup',
              path: ['phone'],
            })
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'validation:requiredGroup',
              path: ['mobile'],
            })
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'validation:requiredGroup',
              path: ['email'],
            })
          }
        }),
    [hasVat, customer]
  )

  const form = useForm({
    defaultValues,
    resolver: zodResolver(schema),
  })

  const isChanged = form.formState.isDirty
  const isValid = form.formState.isValid

  const styleChanged = useAnimatedOpacity(isChanged || !isValid)

  const { isConfirmed, canConfirm, handleConfirm } = useConfirmCustomer()

  const styleConfirmed = useAnimatedOpacity(!isConfirmed)
  const setOrderRequirements = useOrderState((s) => s.setRequirements)
  const setConfirmed = (bool: boolean) =>
    setOrderRequirements('customerDetails', bool)

  const setCustomerData = useSession((s) => s.setCustomerData)
  const patch = trpc.customer.patch.useMutation({
    onSuccess: (res) => {
      setCustomerData(res)
      if (canConfirm) handleConfirm()
      onClose()
    },
  })

  const onConfirm = form.handleSubmit((values) => {
    if (canConfirm) handleConfirm()
    close()
  })

  const onSubmit = form.handleSubmit((values) => {
    if (!customer) return
    patch.mutate({
      id: customer.id,
      ...values,
      birth_date: values.birth_date?.toISOString().split('T')[0],
    })
  })

  const onClose = () => {
    form.reset()
    close()
  }

  useEffect(() => {
    if (open) {
      form.reset(defaultValues)
    }
  }, [open])

  useEffect(() => {
    if (isChanged) setConfirmed(false)
  }, [isChanged])

  return (
    <ModalBottom
      open={open}
      close={close}
      title={t('modals:customer.title')}
      desc={t('modals:customer.desc')}
      body={
        <FormProvider {...form}>
          <View className="gap-6 px-16">
            <FormInput
              control={form.control}
              label={t('user:firstName')}
              name="first_name"
              autoCorrect={false}
              autoCapitalize="words"
              next="last_name"
            />
            <FormInput
              control={form.control}
              label={t('user:lastName')}
              name="last_name"
              autoCorrect={false}
              autoCapitalize="words"
              next="birth_date"
            />
            <FormInputDate
              control={form.control}
              label={t('user:birthDate')}
              name="birth_date"
              next="email"
              min={BirthdateValues.min}
              max={BirthdateValues.max}
              defaultDate={BirthdateValues.default}
            />
            <FormInput
              control={form.control}
              label={t('user:email')}
              name="email"
              next="mobile"
            />
            <FormInput
              control={form.control}
              label={t('user:mobile')}
              name="mobile"
              next="phone"
            />
            <FormInput
              control={form.control}
              label={t('user:landline')}
              name="phone"
              next={hasVat ? 'address.vat' : 'address.street'}
            />
            {/* <FormInput
              control={form.control}
              label={t('user:name')}
              name="name"
            />
            <FormInput
              control={form.control}
              label={t('user:vat')}
              name="vatId"
            /> */}
            {hasVat && (
              <FormInput
                control={form.control}
                label={t('user:vat')}
                name="address.vat"
                next="address.street"
              />
            )}
            <View className="flex-row gap-6">
              <FormInput
                control={form.control}
                label={t('user:street')}
                name="address.street"
                next="address.street2"
                className="w-3/5"
              />
              <FormInput
                control={form.control}
                label={t('user:street2')}
                labelWidth={40}
                name="address.street2"
                next="address.city"
                className="flex-1"
              />
            </View>
            <View className="flex-row gap-6">
              <FormInput
                control={form.control}
                label={t('user:city')}
                name="address.city"
                next="address.zip"
                className="w-3/5"
              />
              <FormInput
                control={form.control}
                label={t('user:zip')}
                labelWidth={40}
                name="address.zip"
                className="flex-1"
              />
            </View>
            <FormSelect
              control={form.control}
              label={t('user:country')}
              name="address.country_code"
              options={countries}
              placeholder={t('user:address.country')}
            />
            {/* <Checkbox
              value={isConfirmed}
              onChange={() => setConfirmed(!isConfirmed)}
              colorActive={colors.green[600]}
              label={t('modals:customer.confirmation')} /> */}
          </View>
        </FormProvider>
      }
      buttons={
        <>
          <Button
            variant="filled"
            label={t('ui:save')}
            onPress={onConfirm}
            disabled={isChanged || !canConfirm}
            style={[styleConfirmed]}
            className={cn(isConfirmed && 'pointer-events-none')}
          />
          {/* <Button variant="outlined" label={t('ui:close')} onPress={onClose} /> */}
          {(isChanged || !isValid) && (
            <Button
              variant="filled"
              label={t('ui:save')}
              iconEnd="arrow-narrow-right"
              loading={patch.isPending}
              onPress={onSubmit}
              style={styleChanged}
            />
          )}
        </>
      }
    />
  )
}
